/**
 * The ×N streak badge (§8.7, DECISIONS.md D141).
 *
 * `current_streak` is capped at 400 rows on its own hot path (`/api/recurring`, every
 * briefing load) — cheap because most streaks never approach that, but a genuinely
 * years-long unbroken one gets reported as a floor (`×400+`) rather than paid for exactly
 * on every load. Clicking a capped badge asks for the real number just this once
 * (`GET /api/tasks/{id}/streak`, `exact_streak` — unbounded, computed only on demand), and
 * once resolved, stays resolved for the rest of this mount — it never needs to ask again.
 *
 * An uncapped badge (the common case) is never clickable; there's nothing to ask for.
 */

import { useState } from "react";
import { api } from "../api";

export function StreakBadge({
  taskId,
  streak,
  capped,
}: {
  taskId: string;
  streak: number;
  capped: boolean;
}) {
  const [resolved, setResolved] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);

  if (streak <= 0) return null;
  if (resolved !== null) {
    return (
      <span className="badge" title="Current streak">
        ×{resolved}
      </span>
    );
  }
  if (!capped) {
    return (
      <span className="badge" title="Current streak">
        ×{streak}
      </span>
    );
  }

  async function resolveExact() {
    setLoading(true);
    try {
      const { streak: exact } = await api.taskStreak(taskId);
      setResolved(exact);
    } catch {
      /* stays capped and clickable — a retry is just clicking it again */
    } finally {
      setLoading(false);
    }
  }

  return (
    <button
      type="button"
      className={`badge streak-capped${loading ? " streak-calculating" : ""}`}
      onClick={() => void resolveExact()}
      disabled={loading}
      aria-label={loading ? "Calculating the exact streak" : `At least ${streak} - click for the exact count`}
      title={
        loading
          ? "Calculating…"
          : "A subtle count, not a headline number (§8.7) - click for the exact one"
      }
    >
      {loading ? "Calculating…" : `×${streak}+`}
    </button>
  );
}
